import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import MainCard from 'components/MainCard';
import useGet from 'hooks/useGet';

import {
  formatCurrency,
  formatDateTime,
  normalizeInvoice,
  resolveProductImage,
  toTitleCase
} from './helpers';

export default function CustomProductInvoiceDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, error, loading } = useGet(
    id ? `/api/admin/custom-product-invoices/${id}` : null
  );

  const invoice = useMemo(
    () => normalizeInvoice(data?.invoice || data?.data || data || {}),
    [data]
  );
  const image = resolveProductImage(invoice.customProduct);

  if (loading) {
    return <Typography>Loading custom product invoice details...</Typography>;
  }

  if (error) {
    return <Alert severity="error">Failed to load custom product invoice details.</Alert>;
  }

  if (!invoice.id) {
    return <Alert severity="warning">Custom product invoice not found.</Alert>;
  }

  return (
    <Stack spacing={2.5}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={1.5}
      >
        <Typography variant="h4">Custom Product Invoice Details</Typography>
        <Button variant="outlined" onClick={() => navigate('/custom-products/invoices')}>
          Back to Invoices
        </Button>
      </Stack>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <MainCard title="Invoice Summary">
            <Stack spacing={1.1}>
              <Typography><strong>Invoice:</strong> {invoice.invoiceNumber || invoice.id}</Typography>
              <Typography><strong>Status:</strong> {toTitleCase(invoice.status)}</Typography>
              <Typography><strong>Order ID:</strong> {invoice.order?.id || '-'}</Typography>
              <Typography><strong>Product:</strong> {invoice.customProduct?.title || '-'}</Typography>
              <Typography><strong>SKU:</strong> {invoice.customProduct?.SKU || '-'}</Typography>
              <Typography><strong>Currency:</strong> {invoice.currency}</Typography>
              <Typography>
                <strong>Total:</strong> {formatCurrency(invoice.currency, invoice.totalAmount)}
              </Typography>
              <Typography><strong>Created:</strong> {formatDateTime(invoice.createdAt)}</Typography>
              <Typography><strong>Updated:</strong> {formatDateTime(invoice.updatedAt)}</Typography>
            </Stack>
          </MainCard>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <MainCard title="Product Preview">
            <Stack spacing={1.5}>
              {image ? (
                <img
                  src={image}
                  alt={invoice.customProduct?.title || 'Custom product'}
                  style={{
                    width: '100%',
                    maxHeight: 240,
                    objectFit: 'cover',
                    borderRadius: 12
                  }}
                />
              ) : null}
              <Typography variant="subtitle1">
                {invoice.customProduct?.title || 'Custom product'}
              </Typography>
              <Typography color="text.secondary">
                {invoice.customProduct?.description || 'No product description available.'}
              </Typography>
            </Stack>
          </MainCard>
        </Grid>
      </Grid>
    </Stack>
  );
}
